import { useState } from 'react';
import { FloatingFormItem, Group, InputDecimal } from '@/components/_common';
import CfopSelectField from '@/components/_common/CfopSelectField';
import {
  IcmsBaseCalculationDeterminationMode,
  IcmsOrigin,
  InvoiceExigibility,
  InvoiceIcmsCstCsosn,
  InvoiceTaxType,
  IpiCst,
  PisCofinsCst,
  TaxRegimeEnum,
} from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItems.types';
import { CompanyInvoiceSettingsForm } from '@/core/domain/invoice/settings/Settings.types';
import {
  Checkbox,
  Col,
  Form,
  Input,
  Row,
  Select,
  Tooltip,
  Typography,
} from 'antd';
import { FormInstance } from 'antd/es/form/Form';
import CompanyTextEditor from '@/components/Companies/CompanyTextEditor';
import TabsCustom from '@/components/_common/TabsCustom';
import { useIntl } from 'react-intl';
import { renderIcmsCstSelect } from '@/utils/functions/renderIcmsCstSelect';
import { Info } from 'react-feather';
import SelectInvoiceCST from '@/components/_common/TaxReform/SelectInvoiceCST';
import SelectInvoiceClassificationTax from '@/components/_common/TaxReform/SelectInvoiceClassificationTax';
import { useCompanyFormContext } from '@/components/Companies/CompaniesFormProvider';
import InvoiceNBSInfiniteSelect from '@/components/_common/InvoiceNBSInfiniteSelect';

interface Props {
  invoiceType: 'nfe' | 'nfs' | 'nfc';
  form: FormInstance<CompanyInvoiceSettingsForm>;
}

const InvoiceTributesDefaultFields = ({ invoiceType, form }: Props) => {
  const intl = useIntl();
  const { Title } = Typography;

  const { useInternalInvoiceApiNfe } = useCompanyFormContext();

  const isAutomaticIbptCalculation = Form.useWatch(
    ['nfs', 'automaticIbptCalculation'],
    form,
  );

  const [key, setKey] = useState<string>();

  const taxRegimeOptions = [
    { value: TaxRegimeEnum.normal, label: 'Regime Normal' },
    { value: TaxRegimeEnum.simple, label: 'Simples Nacional' },
    {
      value: TaxRegimeEnum.simplesNationalMei,
      label: 'Simples Nacional - Microempreendedor Individual - MEI',
    },
    {
      value: TaxRegimeEnum.simpleNationalExcessSublimitGrossRevenue,
      label: 'Simples nacional com excesso do sublimite',
    },
  ];

  const taxTypeOptions = [
    { value: InvoiceTaxType.EXEMPT_ISS, label: 'Isenção ISS' },
    { value: InvoiceTaxType.IMMUNE, label: 'Imune' },
    {
      value: InvoiceTaxType.NON_INCIDENCE_IN_CITY,
      label: 'Sem incidência na cidade',
    },
    { value: InvoiceTaxType.NON_TAXABLE, label: 'Não tributável' },
    { value: InvoiceTaxType.WITHHELD, label: 'Retido' },
    {
      value: InvoiceTaxType.TAXABLE_WITHIN_CITY,
      label: 'Tributável dentro da cidade',
    },
    {
      value: InvoiceTaxType.TAXABLE_OUTSIDE_CITY,
      label: 'Tributável fora da cidade',
    },
    {
      value: InvoiceTaxType.TAXABLE_WITHIN_CITY_BY_TAKER,
      label: 'Tributável dentro da cidade pelo comprador',
    },
  ];

  const exigibilityOptions = [
    { value: InvoiceExigibility.EXIGIBLE, label: 'Exigível' },
    { value: InvoiceExigibility.NON_INCIDENCE, label: 'Não incidência' },
    {
      value: InvoiceExigibility.EXEMPTION,
      label: 'Isenção',
    },
    { value: InvoiceExigibility.EXPORT, label: 'Exportar' },
    { value: InvoiceExigibility.IMMUNITY, label: 'Imunidade' },
    {
      value: InvoiceExigibility.JUDICIAL_SUSPENSION,
      label: 'Suspensão Judicial',
    },
    {
      value: InvoiceExigibility.ADMINISTRATIVE_SUSPENSION,
      label: 'Suspensão administrativa',
    },
  ];

  const originOptions = [
    {
      value: IcmsOrigin.NATIONAL_EXCEPT_CODES_3458,
      label: '0 - Nacional, exceto as indicadas nos códigos 3, 4, 5 e 8',
    },
    {
      value: IcmsOrigin.FOREIGN_DIRECT_IMPORT_EXCEPT_CODE_6,
      label:
        '1 - Estrangeira - Importação direta, exceto a indicada no código 6',
    },
    {
      value: IcmsOrigin.FOREIGN_ACQUIRED_IN_DOMESTIC_MARKET_EXCEPT_CODE_7,
      label:
        '2 - Estrangeira - Adquirida no mercado interno, exceto a indicada no código 7',
    },
    {
      value: IcmsOrigin.NATIONAL_CONTENT_IMPORTATION_40_70,
      label:
        '3 - Nacional, mercadoria ou bem com Conteúdo de Importação superior a 40% e inferior a 70%',
    },
    {
      value: IcmsOrigin.NATIONAL_BASIC_PRODUCTION_PROCESS,
      label:
        '4 - Nacional, cuja produção tenha sido feita em conformidade com os processos produtivos básicos de que tratam as legislações citadas nos Ajustes',
    },
    {
      value: IcmsOrigin.NATIONAL_CONTENT_IMPORTATION_40,
      label:
        '5 - Nacional, mercadoria ou bem com Conteúdo de Importação inferior ou a 40%',
    },
    {
      value: IcmsOrigin.FOREIGN_DIRECT_IMPORT_NO_NATIONAL_SIMILAR_CAMEX,
      label:
        '6 - Estrangeira - Importação direta, sem similar nacional, constante em lista de CAMEX e gás natural',
    },
    {
      value:
        IcmsOrigin.FOREIGN_ACQUIRED_IN_DOMESTIC_MARKET_NO_NATIONAL_SIMILAR_CAMEX,
      label:
        '7 - Estrangeira - Adquirida no mercado interno, sem similar nacional, constante lista CAMEX e gás natural',
    },
    {
      value: IcmsOrigin.NATIONAL_CONTENT_IMPORTATION_70,
      label: '8 - Nacional, mercadoria',
    },
  ];

  const ipiCstOptions = [
    {
      value: IpiCst.INPUT_RECOVERY,
      label: '00 - Entrada com recuperação de crédito',
    },
    {
      value: IpiCst.INPUT_TAXED_ZERO_RATE,
      label: '01 - Entrada tributada com alíquota zero',
    },
    {
      value: IpiCst.INPUT_EXEMPT,
      label: '02 - Entrada isenta',
    },
    {
      value: IpiCst.INPUT_NON_TAXABLE,
      label: '03 - Entrada não-tributada',
    },
    {
      value: IpiCst.INPUT_IMMUNE,
      label: '04 - Entrada imune',
    },
    {
      value: IpiCst.INPUT_SUSPENDED,
      label: '05 - Entrada com suspensão',
    },
    {
      value: IpiCst.OTHER_INPUTS,
      label: '49 - Outras entradas',
    },
    {
      value: IpiCst.OUTPUT_TAXED,
      label: '50 - Saída tributada',
    },
    {
      value: IpiCst.OUTPUT_TAXED_ZERO_RATE,
      label: '51 - Saída tributada com alíquota zero',
    },
    {
      value: IpiCst.OUTPUT_EXEMPT,
      label: '52 - Saída isenta',
    },
    {
      value: IpiCst.OUTPUT_NON_TAXABLE,
      label: '53 - Saída não-tributada',
    },
    {
      value: IpiCst.OUTPUT_IMMUNE,
      label: '54 - Saída imune',
    },
    {
      value: IpiCst.OUTPUT_SUSPENDED,
      label: '55 - Saída com suspensão',
    },
    {
      value: IpiCst.OTHER_OUTPUTS,
      label: '99 - Outras saídas',
    },
  ];

  const pisCstOptions = [
    {
      value: PisCofinsCst.TAXABLE_OPERATION_NORMAL_RATE,
      label:
        '01 - Operação Tributável (base de cálculo = valor da operação alíquota normal (cumulativo/não cumulativo))',
    },
    {
      value: PisCofinsCst.TAXABLE_OPERATION_DIFFERENTIATED_RATE,
      label:
        '02 - Operação Tributável (base de cálculo = valor da operação (alíquota diferenciada))',
    },
    {
      value: PisCofinsCst.TAXABLE_OPERATION_QUANTITY_RATE,
      label:
        '03 - Operação Tributável (base de cálculo = quantidade vendida x alíquota por unidade de produto)',
    },
    {
      value: PisCofinsCst.TAXABLE_OPERATION_MONOPHASIC,
      label: '04 - Operação Tributável (tributação monofásica (alíquota zero))',
    },
    {
      value: PisCofinsCst.TAXABLE_OPERATION_SUBSTITUTION,
      label: '05 - Operação Tributável (Substituição Tributária)',
    },
    {
      value: PisCofinsCst.TAXABLE_OPERATION_ZERO_RATE,
      label: '06 - Operação Tributável (alíquota zero)',
    },

    {
      value: PisCofinsCst.EXEMPT_OPERATION,
      label: '07 - Operação Isenta da Contribuição',
    },
    {
      value: PisCofinsCst.NON_INCIDENCE_OPERATION,
      label: '08 - Operação Sem Incidência da Contribuição',
    },
    {
      value: PisCofinsCst.OPERATION_WITH_SUSPENSION,
      label: '09 - Operação com Suspensão da Contribuição',
    },
    {
      value: PisCofinsCst.OTHER_OUTPUT_OPERATIONS,
      label: '49 - Outras Operações de Saída',
    },
    {
      value: PisCofinsCst.OPERATION_WITH_INTERNAL_TAX_CREDIT,
      label:
        '50 - Operação com Direito a Crédito - Vinculada Exclusivamente a Receita Tributada no Mercado Interno',
    },
    {
      value: PisCofinsCst.OPERATION_WITH_NON_TAXABLE_INTERNAL_REVENUE_CREDIT,
      label:
        '51 - Operação com Direito a Crédito - Vinculada Exclusivamente a Receita Não Tributada no Mercado Interno',
    },
    {
      value: PisCofinsCst.OPERATION_WITH_EXPORT_REVENUE_CREDIT,
      label:
        '52 - Operação com Direito a Crédito – Vinculada Exclusivamente a Receita de Exportação',
    },
    {
      value:
        PisCofinsCst.OPERATION_WITH_TAXED_AND_NON_TAXABLE_INTERNAL_REVENUE_CREDIT,
      label:
        '53 - Operação com Direito a Crédito - Vinculada a Receitas Tributadas e Não-Tributadas no Mercado Interno',
    },
    {
      value:
        PisCofinsCst.OPERATION_WITH_TAXED_INTERNAL_AND_EXPORT_REVENUE_CREDIT,
      label:
        '54 - Operação com Direito a Crédito - Vinculada a Receitas Tributadas no Mercado Interno e de Exportação',
    },
    {
      value:
        PisCofinsCst.OPERATION_WITH_NON_TAXABLE_INTERNAL_AND_EXPORT_REVENUE_CREDIT,
      label:
        '55 - Operação com Direito a Crédito - Vinculada a Receitas Não-Tributadas no Mercado Interno e de Exportação',
    },
    {
      value:
        PisCofinsCst.OPERATION_WITH_TAXED_AND_NON_TAXABLE_INTERNAL_EXPORT_REVENUE_CREDIT,
      label:
        '56 - Operação com Direito a Crédito - Vinculada a Receitas Tributadas e Não-Tributadas no Mercado Interno, e de Exportação',
    },
    {
      value: PisCofinsCst.PRESUMED_CREDIT_INTERNAL_REVENUE_TAXED,
      label:
        '60 - Crédito Presumido - Operação de Aquisição Vinculada Exclusivamente a Receita Tributada no Mercado Interno',
    },
    {
      value: PisCofinsCst.PRESUMED_CREDIT_INTERNAL_NON_TAXABLE_REVENUE,
      label:
        '61 - Crédito Presumido - Operação de Aquisição Vinculada Exclusivamente a Receita Não-Tributada no Mercado Interno',
    },
    {
      value: PisCofinsCst.PRESUMED_CREDIT_EXPORT_REVENUE,
      label:
        '62 - Crédito Presumido - Operação de Aquisição Vinculada Exclusivamente a Receita de Exportação',
    },
    {
      value:
        PisCofinsCst.PRESUMED_CREDIT_TAXED_AND_NON_TAXABLE_INTERNAL_REVENUE,
      label:
        '63 - Crédito Presumido - Operação de Aquisição Vinculada a Receitas Tributadas e Não-Tributadas no Mercado Interno',
    },
    {
      value: PisCofinsCst.PRESUMED_CREDIT_TAXED_INTERNAL_AND_EXPORT_REVENUE,
      label:
        '64 - Crédito Presumido - Operação de Aquisição Vinculada a Receitas Tributadas no Mercado Interno e de Exportação',
    },
    {
      value:
        PisCofinsCst.PRESUMED_CREDIT_NON_TAXABLE_INTERNAL_AND_EXPORT_REVENUE,
      label:
        '65 - Crédito Presumido - Operação de Aquisição Vinculada a Receitas Não-Tributadas no Mercado Interno e de Exportação',
    },
    {
      value:
        PisCofinsCst.PRESUMED_CREDIT_TAXED_AND_NON_TAXABLE_INTERNAL_EXPORT_REVENUE,
      label:
        '66 - Crédito Presumido - Operação de Aquisição Vinculada a Receitas Tributadas e Não-Tributadas no Mercado Interno, e de Exportação',
    },
    {
      value: PisCofinsCst.OTHER_PRESUMED_CREDITS,
      label: '67 - Crédito Presumido - Outras Operações',
    },
    {
      value: PisCofinsCst.ACQUISITION_WITHOUT_CREDIT,
      label: '70 - Operação de Aquisição sem Direito a Crédito',
    },
    {
      value: PisCofinsCst.ACQUISITION_WITH_EXEMPTION,
      label: '71 - Operação de Aquisição com Isenção',
    },
    {
      value: PisCofinsCst.ACQUISITION_WITH_SUSPENSION,
      label: '72 - Operação de Aquisição com Suspensão',
    },
    {
      value: PisCofinsCst.ACQUISITION_WITH_ZERO_RATE,
      label: '73 - Operação de Aquisição a Alíquota Zero',
    },
    {
      value: PisCofinsCst.ACQUISITION_WITHOUT_INCIDENCE,
      label: '74 - Operação de Aquisição; sem Incidência da Contribuição',
    },
    {
      value: PisCofinsCst.ACQUISITION_WITH_SUBSTITUTION,
      label: '75 - Operação de Aquisição por Substituição Tributária',
    },
    {
      value: PisCofinsCst.OTHER_INPUT_OPERATIONS,
      label: '98 - Outras Operações de Entrada',
    },
    {
      value: PisCofinsCst.OTHER_OPERATIONS,
      label: '99 - Outras Operações',
    },
  ];

  const icmsModalityOptions = [
    {
      value: IcmsBaseCalculationDeterminationMode.MARGIN_VALUE_ADDED,
      label: '0 - Margem Valor Agregado (%)',
    },
    {
      value: IcmsBaseCalculationDeterminationMode.TABLE_PRICE_MAX,
      label: '1 - Pauta (valor)',
    },
    {
      value: IcmsBaseCalculationDeterminationMode.LIST_PRICE_MAX,
      label: '2 - Preço Tabelado Máx. (valor)',
    },
    {
      value: IcmsBaseCalculationDeterminationMode.OPERATION_VALUE,
      label: '3 - Valor da operação',
    },
  ];

  const watchTaxRegime = Form.useWatch([invoiceType, 'taxRegimeDefault'], form);
  // TODO: Não entrará em vigor em 2026
  // const watchSelectiveTaxCst = Form.useWatch(
  //   [invoiceType, 'selectiveTaxCstDefault'],
  //   form,
  // );
  const watchIbsCbsCst = Form.useWatch([invoiceType, 'ibsCbsCstDefault'], form);
  const watchIcmsCst = Form.useWatch([invoiceType, 'icmsCstDefault'], form);

  const pisFields = () => {
    return (
      <>
        <Col span={24} md={8} xl={8}>
          <FloatingFormItem
            name={[invoiceType, 'pisCstDefault']}
            label="PIS CST"
          >
            <Select allowClear options={pisCstOptions} />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={8} xl={8}>
          <FloatingFormItem
            name={[invoiceType, 'pisAliquotDefault']}
            label="Alíquota do PIS (%)"
          >
            <InputDecimal fractions={4} max={100} />
          </FloatingFormItem>
        </Col>
      </>
    );
  };

  const cofinsFields = () => {
    return (
      <>
        <Col span={24} md={8} xl={8}>
          <FloatingFormItem
            name={[invoiceType, 'cofinsCstDefault']}
            label="COFINS CST"
          >
            <Select allowClear options={pisCstOptions} />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={8} xl={8}>
          <FloatingFormItem
            name={[invoiceType, 'cofinsAliquotDefault']}
            label="Alíquota do COFINS (%)"
          >
            <InputDecimal fractions={4} max={100} />
          </FloatingFormItem>
        </Col>
      </>
    );
  };

  return (
    <TabsCustom onChange={setKey} activeKey={key}>
      <TabsCustom.Item label="Geral" key="general">
        <Row gutter={[16, 16]}>
          {invoiceType !== 'nfs' && (
            <Col span={24} md={8} xl={8}>
              <FloatingFormItem name={[invoiceType, 'cfopId']} label="CFOP">
                <CfopSelectField />
              </FloatingFormItem>
            </Col>
          )}

          {invoiceType === 'nfs' && (
            <>
              <Col span={24} md={12} xl={6}>
                <FloatingFormItem
                  name={[invoiceType, 'issqnServiceCodeDefault']}
                  label="Código de serviço"
                >
                  <Input />
                </FloatingFormItem>
              </Col>

              <Col span={24} md={12} xl={6}>
                <FloatingFormItem
                  name={[invoiceType, 'taxCodeDefault']}
                  label="Código de tributação"
                >
                  <Input />
                </FloatingFormItem>
              </Col>

              <Col span={24} md={12} xl={6}>
                <FloatingFormItem
                  name={[invoiceType, 'nbsCodeDefault']}
                  label="Código NBS"
                >
                  <InvoiceNBSInfiniteSelect />
                </FloatingFormItem>

                <span>
                  Em caso de dúvida, procure mais informações com sua
                  contabilidade
                </span>
              </Col>
            </>
          )}

          <Col span={24} md={12} xl={6}>
            <FloatingFormItem
              name={[invoiceType, 'unitMeasurementDefault']}
              label="Unidade"
            >
              <Input />
            </FloatingFormItem>
          </Col>
        </Row>
      </TabsCustom.Item>

      {invoiceType !== 'nfs' && [
        <TabsCustom.Item label="ICMS" key="icms">
          <Row gutter={[16, 16]}>
            <Col span={24} md={8} xl={8}>
              <FloatingFormItem
                name={[invoiceType, 'taxRegimeDefault']}
                label="Regime tributário"
                getValueFromEvent={(value) => {
                  form.setFieldValue(
                    [invoiceType, 'icmsCstDefault'],
                    undefined,
                  );
                  form.setFieldValue(
                    [invoiceType, 'simpleNationalCreditRateDefault'],
                    undefined,
                  );

                  return value;
                }}
              >
                <Select options={taxRegimeOptions} />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={8} xl={8}>
              {renderIcmsCstSelect(watchTaxRegime, invoiceType)}
            </Col>

            <Col span={24} md={8} xl={8}>
              <FloatingFormItem
                name={[invoiceType, 'icmsOriginDefault']}
                label="Origem"
              >
                <Select allowClear options={originOptions} />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={8} xl={8}>
              <FloatingFormItem
                name={[invoiceType, 'icmsAliquotDefault']}
                label="Aliquota do ICMS"
              >
                <InputDecimal fractions={2} max={100} />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={8} xl={8}>
              <FloatingFormItem
                name={[
                  invoiceType,
                  'icmsBaseCalculationDeterminationModeDefault',
                ]}
                label="Modalidade de determinação da BC"
              >
                <Select allowClear options={icmsModalityOptions} />
              </FloatingFormItem>
            </Col>

            {watchTaxRegime === TaxRegimeEnum.simple &&
              [
                InvoiceIcmsCstCsosn.TAXATION_UNDER_SIMPLES_WITH_CREDIT_PERMISSION,
                InvoiceIcmsCstCsosn.SIMPLES_NACIONAL_WITH_CREDIT_PERMISSION_AND_ICMS_BY_TAX_SUBSTITUTION,
              ].includes(watchIcmsCst) && (
                <Col span={24} md={8} xl={8}>
                  <FloatingFormItem
                    name={[invoiceType, 'simpleNationalCreditRateDefault']}
                    label="Alíquota de crédito do Simples Nacional"
                    extra="Aplicável quando CSOSN 101 e 201"
                  >
                    <InputDecimal fractions={4} />
                  </FloatingFormItem>
                </Col>
              )}
          </Row>
        </TabsCustom.Item>,

        <TabsCustom.Item label="IPI" key="ipi">
          <Row gutter={[16, 16]}>
            <Col span={24} md={8} xl={8}>
              <FloatingFormItem
                name={[invoiceType, 'ipiCstDefault']}
                label="IPI CST"
              >
                <Select
                  allowClear
                  options={ipiCstOptions?.map((code) => ({
                    value: code.value,
                    label: code.label,
                  }))}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={8} xl={8}>
              <FloatingFormItem
                name={[invoiceType, 'ipiAliquotDefault']}
                label="Alíquota do IPI"
              >
                <InputDecimal fractions={4} max={100} />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={8} xl={8}>
              <FloatingFormItem
                name={[invoiceType, 'ipiLegalClassificationCodeDefault']}
                label="Cód. enquadramento"
              >
                <Input />
              </FloatingFormItem>
            </Col>
          </Row>
        </TabsCustom.Item>,

        <TabsCustom.Item label="PIS" key="pis">
          <Row gutter={[16, 16]}>{pisFields()}</Row>
        </TabsCustom.Item>,

        <TabsCustom.Item label="COFINS" key="cofins">
          <Row gutter={[16, 16]}>{cofinsFields()}</Row>
        </TabsCustom.Item>,
      ]}

      <TabsCustom.Item label="Retenção" key="irpjCsll">
        <Row gutter={[16, 16]}>
          <Col span={24} md={8} xl={8}>
            <FloatingFormItem
              name={[invoiceType, 'csllAliquotDefault']}
              label="Alíquota do CSLL (%)"
            >
              <InputDecimal fractions={4} max={100} />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={8} xl={8}>
            <FloatingFormItem
              name={[invoiceType, 'irrfAliquotDefault']}
              label="Alíquota do IRFF (%)"
            >
              <InputDecimal fractions={4} max={100} />
            </FloatingFormItem>
          </Col>

          {pisFields()}

          {cofinsFields()}
        </Row>
      </TabsCustom.Item>

      {invoiceType === 'nfs' && (
        <TabsCustom.Item label="ISS" key="iss">
          <Row gutter={[16, 16]}>
            <Col span={24} md={12} xl={6}>
              <FloatingFormItem
                name={[invoiceType, 'taxTypeDefault']}
                label="Tipo de tributação"
              >
                <Select options={taxTypeOptions} allowClear />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} xl={6}>
              <FloatingFormItem
                name={[invoiceType, 'exigibilityDefault']}
                label="Exigibilidade"
              >
                <Select options={exigibilityOptions} allowClear />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} xl={6}>
              <FloatingFormItem
                label={'Retido'}
                name={[invoiceType, 'withheldDefault']}
              >
                <Select
                  allowClear
                  style={{ width: '100' }}
                  options={[
                    {
                      value: 'true',
                      label: intl.formatMessage({
                        id: 'invoice.domain.true',
                      }),
                    },
                    {
                      value: 'false',
                      label: intl.formatMessage({
                        id: 'invoice.domain.false',
                      }),
                    },
                  ]}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} xl={6}>
              <FloatingFormItem
                name={[invoiceType, 'rateDefault']}
                label="Alíquota"
              >
                <InputDecimal fractions={4} max={100} />
              </FloatingFormItem>
            </Col>

            <Col span={24}>
              <Group>
                <Form.Item
                  name={[invoiceType, 'calculateWithheldIssInWorkOrder']}
                  valuePropName="checked"
                  noStyle
                >
                  <Checkbox>
                    Utilizar o valor do ISS retido como desconto na geração do
                    financeiro do orçamento.
                  </Checkbox>
                </Form.Item>

                <Tooltip title="Quando marcado, o valor do ISS retido será calculado pelo percentual da alíquota informada sobre o total dos serviços do orçamento e será enviado como desconto ($) no financeiro do orçamento. Assim não serão considerados os valores de peças.">
                  <Info size={20} cursor="pointer" />
                </Tooltip>
              </Group>
            </Col>

            <Col span={24}>
              <Form.Item
                name={[invoiceType, 'zeroOutIssValueNotWithheld']}
                valuePropName="checked"
                noStyle
              >
                <Checkbox>
                  Zerar o valor do ISS quando não houver retenção e existir
                  alíquota informada ao gerar a nota ou calcular o tributo da
                  NFS-e.
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </TabsCustom.Item>
      )}

      {/* TODO: Não entrará em vigor em 2026 */}
      {/* <TabsCustom.Item label="IS" key="is">
        <Row gutter={[16, 16]}>
          <Col span={24} sm={12} md={9}>
            <FloatingFormItem
              name={[invoiceType, 'selectiveTaxCstDefault']}
              label="CST IS"
              getValueFromEvent={(value) => {
                form.setFieldValue(
                  [invoiceType, 'selectiveTaxClassificationCodeDefault'],
                  undefined,
                );
                return value;
              }}
            >
              <SelectInvoiceCST
                queryParams={{ cstType: 'SELECTIVE_TAX' }}
                allowClear
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={12} md={9}>
            <FloatingFormItem
              label="Classificação Tributária IS"
              name={[invoiceType, 'selectiveTaxClassificationCodeDefault']}
            >
              <SelectInvoiceClassificationTax
                queryParams={{
                  classificationType: 'SELECTIVE_TAX',
                  cstCode: watchSelectiveTaxCst,
                }}
                allowClear
                disabled={!watchSelectiveTaxCst}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={12} md={6}>
            <FloatingFormItem
              name={[invoiceType, 'selectiveTaxAliquotDefault']}
              label="Alíquota IS (%)"
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>
        </Row>
      </TabsCustom.Item> */}

      <TabsCustom.Item label="IBS/CBS" key="ibsCbs">
        <Row gutter={[16, 16]}>
          <Col span={24} sm={12}>
            <FloatingFormItem
              name={[invoiceType, 'ibsCbsCstDefault']}
              label="CST IBS/CBS"
              getValueFromEvent={(value) => {
                form.setFieldValue(
                  [invoiceType, 'ibsCbsTaxClassificationDefault'],
                  undefined,
                );
                return value;
              }}
            >
              <SelectInvoiceCST
                queryParams={{ cstType: 'IBS_CBS' }}
                allowClear
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={12}>
            <FloatingFormItem
              name={[invoiceType, 'ibsCbsTaxClassificationDefault']}
              label="Classificação Tributária"
            >
              <SelectInvoiceClassificationTax
                queryParams={{
                  classificationType: 'IBS_CBS',
                  cstCode: watchIbsCbsCst,
                }}
                allowClear
                disabled={!watchIbsCbsCst}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={12} md={6}>
            <FloatingFormItem
              name={[invoiceType, 'ibsUfAliquotDefault']}
              label="Alíquota UF IBS (%)"
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={12} md={6}>
            <FloatingFormItem
              name={[invoiceType, 'ibsMunicipalAliquotDefault']}
              label="Alíquota Municipal IBS (%)"
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={12} md={6}>
            <FloatingFormItem
              name={[invoiceType, 'cbsAliquot']}
              label="Alíquota CBS (%)"
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>
        </Row>
      </TabsCustom.Item>

      <TabsCustom.Item label="Dados Adicionais" key="addData">
        <Row gutter={[16, 16]}>
          <>
            <Title level={4}>
              Informações Adicionais de Interesse do Fisco
            </Title>

            <Col span={24}>
              <Form.Item
                name={[
                  invoiceType,
                  invoiceType === 'nfs'
                    ? 'companyAdditionalDataDefault'
                    : 'taxAdditionalDataDefault',
                ]}
              >
                <CompanyTextEditor
                  isNfs={invoiceType === 'nfs'}
                  showApproximateTaxValue={!!useInternalInvoiceApiNfe}
                  isAutomaticIbptCalculation={isAutomaticIbptCalculation}
                />
              </Form.Item>
            </Col>
          </>

          {invoiceType !== 'nfs' && (
            <>
              <Title level={4}>
                Informações Adicionais de Interesse do Contribuinte
              </Title>

              <Col span={24}>
                <Form.Item name={[invoiceType, 'companyAdditionalDataDefault']}>
                  <CompanyTextEditor
                    showApproximateTaxValue={!!useInternalInvoiceApiNfe}
                  />
                </Form.Item>
              </Col>
            </>
          )}

          {invoiceType === 'nfe' && (
            <>
              <Title level={4}>Dados adicionais devolução</Title>
              <Col span={24}>
                <Form.Item
                  name={[
                    invoiceType,
                    'additionalPurchaseReturnInformationDefault',
                  ]}
                >
                  <CompanyTextEditor />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>
      </TabsCustom.Item>
    </TabsCustom>
  );
};

export default InvoiceTributesDefaultFields;
